import {
  EmptyData,
  Flex,
  FlexColumn, FormConfigurationProvider, IconButton,
  isUserWithRoles, LinkButton,
  MultiSelectInput, SecondaryButton,
  SectionIconButton, SubHeaderContainerProps,
  Table, TableIcon,
  TextSort, ToastStatus,
  Tooltip, useCombinedUser,
  useDeleteAlert, useIsXOverflow,
  UserRoles, useStyledTheme,
  useToastNotifications,
  useWarningAlert,
} from '@equifinance/shared';
import styled, { CSSObject } from 'styled-components';
import {
  PropsWithChildren,
  useCallback, useEffect, useRef, useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import { useManageUserContext } from '../../../../contexts/manageUser';
import { statusFilterCss, SubHeaderContainer, SubHeaderFilters } from '../../../CasesTable/CasesTable';
import { PlusIcon } from '../../../Account/components/Icons';
import { PaddedSection } from '../../../Account/components/Section';
import { BrokerUser, BrokerUserStatus } from '../../../../contexts/broker';
import BrokerUserStatusLabel from '../../components/BrokerUserStatus';
import { BrokerUserDetails } from './BrokerUserDetails';
import UnlockIcon from '../../../Icons/UnlockIcon';
import SendIcon from '../../../Icons/SendIcon';

export const UserMamagement = 'user-management';

const mutateSearchValue = (value: string) => {
  const allowedChars = /[A-Za-z0-9@+-._/]/g;
  const filtered = value.match(allowedChars);
  return filtered ? filtered.join('') : '';
};

const SubHeaderFilter = styled(SubHeaderFilters)`
  width: 100%;
`;

const CellValueContainer = styled.div`
  max-width: 100%;
`;

const CellValueText = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function SubHeader({ searchComponent, children }: SubHeaderContainerProps) {
  const { companyId, addNewUser } = useManageUserContext();

  const onAddUser = () => {
    const emptyUser: BrokerUser = {
      firstName: '',
      surname: '',
      companyId,
      id: uuid(),
      isAdmin: false,
      telephoneNumber: '',
      emailAddress: '',
      status: 'OPEN',
    };
    addNewUser(emptyUser);
  };

  return (
    <SubHeaderContainer>
      <SubHeaderFilter>
        <FormConfigurationProvider isReadOnlyInput={false}>
          {searchComponent}
          {children}
        </FormConfigurationProvider>
      </SubHeaderFilter>
      <SecondaryButton onClick={onAddUser} small>
        <PlusIcon />
        Add
      </SecondaryButton>
    </SubHeaderContainer>
  );
}

const ActionsColumnContainer = styled(Flex)`${({ theme }) => `
  width: 100%;
  max-width: 100%;
  align-items: center;
  justify-content: end;
  padding: ${theme.padding[1]};
  gap: ${theme.padding[5]};

  ${SectionIconButton} {
    color: ${theme.colours.primary[1]};
    :hover:not([disabled]) {
      transform: none;
    }
  }
`}`;

const ActionButton = styled(IconButton)`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  ${TableIcon} {
    font-size: ${theme.fontSize[3]};
  }
`}`;

const TableSendIcon = styled(TableIcon) <{ fontSize?: number }>`${({ theme, fontSize }) => `
  transform: rotate(45deg);
  display: inline-block;
  font-size: ${fontSize ? `${fontSize}px` : theme.fontSize[3]};
`}`;

const stickyColumn = {
  style: {
    '&:is(:last-of-type)': {
      position: 'sticky',
      right: 0,
    },
  } as CSSObject,
};

function ActionsColumn({ row, children }: PropsWithChildren<{ row: BrokerUser }>) {
  const warningAlert = useWarningAlert();
  const deleteAlert = useDeleteAlert();
  const {
    deleteUser, deactivateUser, reactivateUser, resendRegistrationToken, setEditUser,
  } = useManageUserContext();
  const { status, id, emailAddress } = row;
  const theme = useStyledTheme();

  const { addToastNotification } = useToastNotifications();

  const showToastMessage = (text: string, type: ToastStatus) => {
    addToastNotification({
      text,
      type,
      showCloseIcon: true,
    });
  };

  const onEdit = () => {
    setEditUser(id);
  };

  const onDelete = () => {
    deleteAlert({
      onConfirm: () => {
        deleteUser(id);
      },
      title: 'Delete',
      content: 'This action cannot be undone.',
      text: 'Are you sure you want to delete this user?',
    });
  };

  const onDeactivate = () => {
    warningAlert({
      onConfirm: async () => {
        await deactivateUser(emailAddress);
      },
      title: 'Deactivate',
      content: 'Are you sure you want to deactivate this user?',
      confirmLabel: 'Deactivate',
    });
  };

  const onActivate = () => {
    warningAlert({
      onConfirm: async () => {
        await reactivateUser(emailAddress);
      },
      title: 'Reactivate',
      content: 'Are you sure you want to reactivate this user?',
      confirmLabel: 'Reactivate',
    });
  };

  const onUnlock = () => {
    warningAlert({
      onConfirm: () => {
      },
      title: 'Unlock',
      content: 'Are you sure you want to unlock this user?',
      titleIconOverride: <UnlockIcon fill={theme.colours.primary[1]} />,
      confirmLabel: 'Unlock',
    });
  };

  const reSendLink = () => {
    warningAlert({
      onConfirm: async () => {
        try {
          await resendRegistrationToken(emailAddress);
          showToastMessage('Email has been re-sent to the user.', ToastStatus.SUCCESS);
        } catch (e) {
          showToastMessage(`We are unable to re-send the verification link at this time.
        If the issue persists please contact the Sales Support team for assistance.`, ToastStatus.WARNING);
        }
      },
      title: 'Resend',
      content: 'Do you want to resend the link?',
      titleIconOverride: <SendIcon fill={theme.colours.primary[1]} />,
      confirmLabel: 'Resend',
    });
  };

  const { user } = useCombinedUser();
  const canManageBrokerUsers = isUserWithRoles([UserRoles.CAN_MANAGE_BROKER_USERS, UserRoles.BROKER_ADMIN], user);
  const canActivateUser = isUserWithRoles([UserRoles.CAN_MANAGE_BROKER_USERS, UserRoles.BROKER_ADMIN, UserRoles.SALES_SUPPORT], user);

  return (
    <>
      <ActionsColumnContainer>
        {(status !== 'OPEN' && canManageBrokerUsers) && (
          <>
            <ActionButton id={`btn-edit-user-${id}`} data-testid={`btn-edit-user-${id}`} onClick={onEdit}>
              <TableIcon className="mi-edit" />
            </ActionButton>
            <Tooltip
              id={`btn-edit-user-${id}`}
              html="Edit user"
              place="bottom"
            />
          </>
        )}

        {status === 'LOCKEDOUT' && (
          <>
            <ActionButton id={`btn-unlock-user-${id}`} data-testid={`btn-unlock-user-${id}`} onClick={onUnlock}>
              <TableIcon className="mi-lock" />
            </ActionButton>
            <Tooltip
              id={`btn-unlock-user-${id}`}
              html="Unlock user"
              place="bottom"
            />
          </>
        )}

        {status === 'PENDING' && (
          <>
            <ActionButton id={`btn-send-link-user-${id}`} data-testid={`btn-send-link-user-${id}`} onClick={reSendLink}>
              <TableSendIcon className="mi-send" />
            </ActionButton>
            <Tooltip
              id={`btn-send-link-user-${id}`}
              html="Resend link"
              place="bottom"
            />
          </>
        )}

        {(status === 'ACTIVE' && canActivateUser) && (
          <>
            <ActionButton id={`btn-deactivate-user-${id}`} data-testid={`btn-deactivate-user-${id}`} onClick={onDeactivate}>
              <TableIcon className="mi-circle-remove" />
            </ActionButton>
            <Tooltip
              id={`btn-deactivate-user-${id}`}
              html="Deactivate user"
              place="bottom"
            />
          </>
        )}

        {(status === 'INACTIVE' && canActivateUser) && (
          <>
            <ActionButton id={`btn-activate-user-${id}`} data-testid={`btn-activate-user-${id}`} onClick={onActivate}>
              <TableIcon className="mi-circle-add" />
            </ActionButton>
            <Tooltip
              id={`btn-activate-user-${id}`}
              html="Reactivate user"
              place="bottom"
            />
          </>
        )}

        {status === 'OPEN' && (
          <>
            <ActionButton id={`btn-delete-${id}`} data-testid={`btn-delete-${row.id}`} onClick={onDelete}>
              <TableIcon className="mi-delete" />
            </ActionButton>
            <Tooltip
              id={`btn-delete-${id}`}
              html="Delete user"
              place="bottom"
            />
          </>
        )}

        {children}
      </ActionsColumnContainer>
    </>
  );
}

function CellValue({ value, id }: { value: string, id: string }) {
  const nameRef = useRef<HTMLDivElement>(null);
  const isTruncated = useIsXOverflow(nameRef);

  return (
    <CellValueContainer id={id}>
      <CellValueText ref={nameRef}>
        {value}
      </CellValueText>
      {isTruncated && (
        <Tooltip
          id={id}
          html={value}
          place="top"
        />
      )}
    </CellValueContainer>
  );
}

export default function UserManagementSection() {
  const theme = useStyledTheme();
  const {
    companyUsers: { users }, openUsers, isFetchingUsers, expandedRows, resetPagination,
  } = useManageUserContext();

  const [filteredUsers, setFilteredUsers] = useState<BrokerUser[]>([]);

  const [adminFilter, setAdminFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const enabledClearFilterBtn = !!(adminFilter.length && statusFilter.length);
  const clearFilters = () => {
    setAdminFilter([]);
    setStatusFilter([]);
  };

  useEffect(() => {
    const filteredUsers = [...openUsers, ...users]
      .filter(({ isAdmin }) => adminFilter.length === 0 || adminFilter.includes(isAdmin ? 'Yes' : 'No'))
      .filter(({ status }) => statusFilter.length === 0 || statusFilter.includes(status)) ?? [];

    setFilteredUsers(filteredUsers);
  }, [users, openUsers, adminFilter, statusFilter]);

  const BrokerUserLabel = useCallback(
    ({ status }: BrokerUser) => (
      <BrokerUserStatusLabel status={status ?? 'OPEN'} />
    ),
    [filteredUsers],
  );

  const columns = [
    {
      name: 'First name',
      sortable: true,
      cell: (brokerUser: BrokerUser) => CellValue({
        value: brokerUser.firstName || '-',
        id: `broker-firstname-${brokerUser.id}`,
      }),
      sortFunction: TextSort<BrokerUser>('firstName'),
    },
    {
      name: 'Last name',
      sortable: true,
      cell: (brokerUser: BrokerUser) => CellValue({
        value: brokerUser.surname || '-',
        id: `broker-surname-${brokerUser.id}`,
      }),
      sortFunction: TextSort<BrokerUser>('surname'),
    },
    {
      name: 'Email address',
      sortable: true,
      cell: (brokerUser: BrokerUser) => CellValue({
        value: brokerUser.emailAddress || '-',
        id: `broker-emailAddress-${brokerUser.id}`,
      }),
      sortFunction: TextSort<BrokerUser>('emailAddress'),
    },
    {
      name: 'Admin',
      sortable: true,
      selector: (brokerUser: BrokerUser) => (brokerUser.isAdmin ? 'Yes' : 'No'),
      sortFunction: TextSort<BrokerUser>('isAdmin'),
    },
    {
      name: 'Status',
      sortable: true,
      cell: (brokerUser: BrokerUser) => BrokerUserLabel(brokerUser),
      sortFunction: TextSort<BrokerUser>('status'),
    },
  ];

  const subHeaderComponent = (
    <>
      <MultiSelectInput
        id="admin-filter"
        value={adminFilter}
        onChange={(value: string[]) => { setAdminFilter(value); }}
        selectedLabel={`Admin${adminFilter.length ? ':' : ''}`}
        disableSearch
        options={{ Yes: 'Yes', No: 'No' }}
        onDeselectOption={(value: string) => {
          setAdminFilter((prev: string[]) => prev.filter((s) => s !== value));
        }}
        containerStyles={statusFilterCss}
      />

      <MultiSelectInput
        id="status-filter"
        value={statusFilter}
        onChange={(value: string[]) => { setStatusFilter(value); }}
        selectedLabel={`Status${statusFilter.length ? ':' : ''}`}
        disableSearch
        options={BrokerUserStatus}
        onDeselectOption={(value: string) => {
          setStatusFilter((prev: string[]) => prev.filter((s) => s !== value));
        }}
        containerStyles={statusFilterCss}
      />

      {enabledClearFilterBtn && (
        <LinkButton
          small
          onClick={clearFilters}
          color={theme.colours.primary[1]}
        >
          Clear all
        </LinkButton>

      )}
    </>
  );

  return (
    <PaddedSection
      id={UserMamagement}
      refName={UserMamagement}
      title="User Management"
      titleIcon={<></>}
    >
      <FlexColumn>
        <Table
          columns={columns}
          data={filteredUsers}
          styles={{
            headCells: stickyColumn,
            cells: stickyColumn,
          }}
          forcedExpandRows={expandedRows}
          SubHeaderContainer={SubHeader}
          subHeaderComponent={subHeaderComponent}
          pagination
          ExpandableCell={ActionsColumn}
          progressPending={isFetchingUsers}
          isFiltered={!!adminFilter.length || !!statusFilter.length}
          noDataComponent={<EmptyData message="Please start a new record." />}
          searchInputProps={{
            mutateValue: mutateSearchValue,
          }}
          expandableRowsComponent={BrokerUserDetails}
          paginationResetDefaultPage={resetPagination}
          paginationDefaultPage={1}
        />
      </FlexColumn>
    </PaddedSection>
  );
}
